import { Fragment, type JSX } from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { AlertTriangle, Info, Megaphone, type LucideIcon } from "lucide-react";
import type {
  AnalyticsAggregateRow,
  AnalyticsFieldKey,
} from "@adlywise/shared";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import type { NameResolution } from "@/features/analytics/hooks/useAnalyticsNameMaps";
import {
  FIELD_LABELS,
  dimensionColumns,
  metricGroupsFor,
  type AnalyticsConfig,
} from "./ConfigurePanel";
import { formatBucket } from "@/features/analytics/utils/runReport";
import { AnalyticsEmptyState } from "./AnalyticsEmptyState";
import { AnalyticsLoadingState } from "./AnalyticsLoadingState";

// Aggregated-results table. Column order matches the user's sketch:
//   <Bucket among the ordered dimension cols> | <metric groups (total/devices)>
// One metric GROUP per configured metric (source=requests → "Requests";
// source=responses → one per selected status), each with two sub-columns
// {total, devices}, so the header is TWO rows. Body cells read
// row["<key>_total"] / row["<key>_devices"] in metric-group order. Entity
// dimensions (campaign/segment/creative/provider) render their resolved name as
// a link to the entity (campaign also shows its icon); other dims are plain
// text. Handles loading / empty / error / not-run, a truncated banner, and a
// distinctDevices≪total note.

type AggregatedResultsTableProps = {
  rows: AnalyticsAggregateRow[];
  config: AnalyticsConfig;
  truncated: boolean;
  // Shown under the money columns ("analytics copy" while they are the
  // ClickHouse copy of the money board).
  moneyLabel?: string | null;
  hasRun: boolean;
  isLoading: boolean;
  errorMessage: string;
  // Names for the dimension ids, resolved by the caller (the page resolves the
  // rows' and the filter's ids once, for the table, the run summary and the
  // export). Without it, ids show as ids.
  resolution?: NameResolution;
};

// A single-column metric. Money and percentages arrive as decimal STRINGS from
// the API, because the figures behind them are exact micro-dollar sums and
// turning them into JS numbers on the way in would quietly undo that. They are
// parsed HERE, for display only.
//
// null is meaningful and distinct from zero: a ratio with no denominator (an
// eCPM over no impressions, a margin over no revenue) has no value, and showing
// it as 0 would read as a real result.
const formatSingleMetric = (
  value: unknown,
  format: "count" | "money" | "percent",
): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (format === "count") return formatMetric(value);
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (format === "percent") return `${n.toFixed(1)}%`;
  // Sub-cent figures are routine here (a CPM over a handful of impressions), so
  // two decimals would render most of them as 0.00.
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.abs(n) > 0 && Math.abs(n) < 0.01 ? 6 : 2,
  });
};

const formatMetric = (value: unknown): string =>
  Number(value ?? 0).toLocaleString("en-US");

// Resolve a campaign's lucide icon component from its stored IconName (a lucide
// PascalCase export, per the new data model). Falls back to Megaphone (the same
// default the campaigns table uses). Mirrors getRowIcon's primary branch without
// the legacy parseItemIcon shim — analytics campaigns always carry a real icon.
const campaignIconComponent = (iconName: string | null): LucideIcon => {
  if (iconName) {
    const icon = (LucideIcons as unknown as Record<string, LucideIcon>)[
      iconName
    ];
    if (icon) return icon;
  }
  return Megaphone;
};

// Entity dimensions link the resolved name to the entity. campaign/segment have
// per-id edit routes; creative/provider have no per-id route → link to the list.
const entityLinkTo = (key: AnalyticsFieldKey, id: string): string | null => {
  switch (key) {
    case "campaign":
      return `/dashboard/campaigns/${id}/edit`;
    case "segment":
      return `/dashboard/segments/${id}/edit`;
    case "creative":
      return `/dashboard/creatives`;
    case "provider":
      return `/dashboard/providers`;
    default:
      return null;
  }
};

const NO_NAMES: NameResolution = { names: {}, campaignIcons: {} };

export const AggregatedResultsTable = ({
  rows,
  config,
  truncated,
  moneyLabel = null,
  hasRun,
  isLoading,
  errorMessage,
  resolution = NO_NAMES,
}: AggregatedResultsTableProps): JSX.Element => {
  const metricGroups = metricGroupsFor(config);
  const dimensions = dimensionColumns(config);
  const { names, campaignIcons } = resolution;

  const nameFor = (key: AnalyticsFieldKey, id: string): string =>
    names[key]?.[id] ?? id;

  // Render a single dimension cell's content: entity dims → linked name (campaign
  // also gets its icon); everything else → resolved plain text. Only linkify when
  // the id resolved to a real entity name.
  const renderDimCell = (key: AnalyticsFieldKey, raw: unknown): JSX.Element => {
    if (raw == null) return <span className="text-(--text-muted)">—</span>;

    // categories is a text[] — resolve + comma-join members, plain text.
    if (Array.isArray(raw)) {
      if (raw.length === 0)
        return <span className="text-(--text-muted)">—</span>;
      return <span>{raw.map((m) => nameFor(key, String(m))).join(", ")}</span>;
    }

    const id = String(raw);
    const to = entityLinkTo(key, id);
    const resolvedName = names[key]?.[id];
    // Only linkify entity dims that actually resolved to a real name.
    if (to && resolvedName) {
      if (key === "campaign") {
        const Icon = campaignIconComponent(campaignIcons[id] ?? null);
        return (
          <Link
            to={to}
            className="inline-flex items-center gap-2 text-(--text-primary) no-underline hover:underline"
            title={resolvedName}
          >
            <Icon
              size={15}
              strokeWidth={1.8}
              className="shrink-0 text-(--brand-primary)"
            />
            <span>{resolvedName}</span>
          </Link>
        );
      }
      return (
        <Link
          to={to}
          className="text-(--text-primary) no-underline hover:underline"
          title={resolvedName}
        >
          {resolvedName}
        </Link>
      );
    }

    return <span>{nameFor(key, id)}</span>;
  };

  if (isLoading) {
    return <AnalyticsLoadingState />;
  }

  if (errorMessage) {
    return (
      <div className="flex min-h-40 items-center justify-center rounded-(--radius-lg) border border-(--status-danger)/30 bg-(--pastel-rose) px-6 py-8 text-center text-[13px] text-(--status-danger)">
        {errorMessage}
      </div>
    );
  }

  if (!hasRun) {
    return (
      <div className="flex min-h-40 items-center justify-center rounded-(--radius-lg) border border-dashed border-(--border-subtle) px-6 py-10 text-center text-[13px] text-(--text-secondary)">
        Configure the report and run it to see results.
      </div>
    );
  }

  if (rows.length === 0) {
    return <AnalyticsEmptyState />;
  }

  return (
    <div className="flex flex-col gap-3">
      {truncated && (
        <div className="flex items-center gap-2 rounded-(--radius-md) border border-(--status-warn)/30 bg-(--pastel-amber) px-4 py-2.5 text-[12.5px] text-(--text-primary)">
          <AlertTriangle size={15} className="shrink-0 text-(--status-warn)" />
          <span>
            Results truncated (ordered): only the first 5,000 rows are shown.
            Narrow the date range or reduce the grouping to see everything.
          </span>
        </div>
      )}

      <DataTable>
        <thead>
          {/* Row 1: bucket and the dimension cols (each spanning both header
              rows), then one spanning cell per metric group: columns are
              always time, then groupings, then metrics. */}
          <tr className={DATA_TABLE_CLASSES.headRow}>
            {dimensions.map((key, i) => (
              <th
                key={key}
                className={`${DATA_TABLE_CLASSES.th} ${i > 0 ? "border-l border-(--border-subtle)" : ""}`}
                rowSpan={2}
              >
                {key === "bucket" ? "Bucket" : FIELD_LABELS[key]}
              </th>
            ))}
            {metricGroups.map((group) => (
              <th
                key={group.key}
                colSpan={group.kind === "pair" ? 2 : 1}
                className={`${DATA_TABLE_CLASSES.th} border-l border-(--border-subtle) text-center`}
              >
                {group.label}
              </th>
            ))}
          </tr>
          {/* Row 2: total / devices under each metric group. */}
          <tr className={DATA_TABLE_CLASSES.headRow}>
            {metricGroups.map((group) => (
              <Fragment key={group.key}>
                {group.kind === "pair" ? (
                  <>
                    <th
                      className={`${DATA_TABLE_CLASSES.thRight} border-l border-(--border-subtle) font-normal`}
                    >
                      total
                    </th>
                    <th className={`${DATA_TABLE_CLASSES.thRight} font-normal`}>
                      devices
                    </th>
                  </>
                ) : (
                  <th
                    className={`${DATA_TABLE_CLASSES.thRight} border-l border-(--border-subtle) font-normal`}
                  >
                    {group.format === "money"
                      ? moneyLabel
                        ? `USD · ${moneyLabel}`
                        : "USD"
                      : group.format === "percent"
                        ? "%"
                        : "total"}
                  </th>
                )}
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.bucket}-${index.toString()}`}
              className={DATA_TABLE_CLASSES.bodyRow}
            >
              {dimensions.map((key, i) =>
                key === "bucket" ? (
                  <td
                    key={key}
                    className={`${DATA_TABLE_CLASSES.td} ${i > 0 ? "border-l border-(--border-subtle)" : ""} text-[13.5px] font-bold text-(--text-primary) whitespace-nowrap`}
                  >
                    {formatBucket(row.bucket, config.bucket)}
                  </td>
                ) : (
                  <td
                    key={key}
                    className={`${DATA_TABLE_CLASSES.td} ${i > 0 ? "border-l border-(--border-subtle)" : ""} text-[13.5px] text-(--text-primary)`}
                  >
                    {renderDimCell(key, row[key])}
                  </td>
                ),
              )}
              {metricGroups.map((group) => (
                <Fragment key={group.key}>
                  {group.kind === "pair" ? (
                    <>
                      <td
                        className={`${DATA_TABLE_CLASSES.tdRight} border-l border-(--border-subtle) text-[13.5px] font-bold text-(--text-primary) tabular-nums`}
                      >
                        {formatMetric(row[`${group.key}_total`])}
                      </td>
                      <td
                        className={`${DATA_TABLE_CLASSES.tdRight} text-[13.5px] font-bold text-(--text-primary) tabular-nums`}
                      >
                        {formatMetric(row[`${group.key}_devices`])}
                      </td>
                    </>
                  ) : (
                    <td
                      className={`${DATA_TABLE_CLASSES.tdRight} border-l border-(--border-subtle) text-[13.5px] font-bold text-(--text-primary) tabular-nums`}
                    >
                      {formatSingleMetric(row[group.key], group.format)}
                    </td>
                  )}
                </Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </DataTable>

      <p className="flex items-start gap-1.5 text-[12px] text-(--text-muted)">
        <Info size={13} className="mt-0.5 shrink-0" />
        <span>
          Distinct devices can be far below total on deviceId-sparse traffic
          (e.g. RTB), where many rows carry no device id (those are excluded
          from the distinct count).
        </span>
      </p>
      <p className="flex items-start gap-1.5 text-[12px] text-(--text-muted)">
        <Info size={13} className="mt-0.5 shrink-0" />
        <span>
          Views, completed views and clicks come from player tracking events,
          recorded since 2026-10-16 for VAST ads served by Adlywise. RTB and
          imported-VAST serves report impressions only, so zero views there
          means not instrumented, not unviewed. VTR = views ÷ impressions within
          the same row.
        </span>
      </p>
    </div>
  );
};
